import { InventoryMessages } from "../constants";
import { CapsuleHitbox } from "../utils/hitbox";
import { Angle, halfπ, τ } from "../utils/math";
import { Vec, type Vector } from "../utils/vector";
import type { ObstacleDefinition } from "./obstacles";

/**
 * Building: press B with 4 wood to build a wooden wall right in front of you, across the direction you're aiming
 * (any direction: it used to snap to the nearest of four), facing you. Trees drop 3 to 5 wood when destroyed. While
 * it's being built (1.2 s) the wall is weak and the browser shows the planks filling in.
 */
export const BUILD = Object.freeze({
    /** the obstacle that gets built */
    wall: "built_wall",
    /** wood per wall */
    cost: 4,
    /** the wall: long enough to hide behind, thin like a barricade (units) */
    length: 11,
    thickness: 2.2,
    /** gap between the player and the wall */
    gap: 1,
    playerRadius: 2.25,
    /** at most one wall this often */
    cooldownMs: 400,
    /** from 15% to full strength */
    durationMs: 1200,
    startFraction: 0.15,
    /** wood a tree drops when destroyed */
    treeWoodMin: 3,
    treeWoodMax: 5,
    /** the circles a wall's hitbox is made of (see `wallHitbox`) */
    hitboxCircles: 15
});

/**
 * A wall's angle (radians, along its length) for an aim: across it, rounded to the 255 steps a turn an angle takes on
 * its way to the browser (`writeRotation`), so the browser draws, and its bullets hit, exactly the wall the server
 * built (1.4 degrees apart at most from the aim's own cross direction).
 */
export const wallAngle = (aim: number): number => {
    const along = Angle.normalize(aim + halfπ);
    const step = Math.round((along / τ + 0.5) * 255);
    return τ * (step / 255 - 0.5);
};

/**
 * A wall's hitbox at any angle. The engine's rectangles only line up with the map (which is why walls could only be
 * built in four directions), so it is a capsule along the wall, the wall's thickness across, its ends rounded like the
 * posts drawn there: a player walking into it is pushed straight back off it, and bullets, grenades and thrown
 * objects hit its row of overlapping circles (flat to within 0.05 units) as they hit any other obstacle.
 */
export const wallHitbox = (position: Vector, angle: number): CapsuleHitbox => {
    const radius = BUILD.thickness / 2;
    const half = Vec.fromPolar(angle, BUILD.length / 2 - radius);
    return new CapsuleHitbox(Vec.sub(position, half), Vec.add(position, half), radius, BUILD.hitboxCircles);
};

export interface BuildSpot {
    /** the wall's centre */
    readonly position: Vector
    /** the wall's angle, along its length (`wallAngle`) */
    readonly angle: number
}

/** where the wall goes: straight ahead of the player, across the direction they're aiming */
export const buildSpot = (position: Vector, aim: number): BuildSpot => {
    const angle = wallAngle(aim);
    const distance = BUILD.playerRadius + BUILD.thickness / 2 + BUILD.gap;
    // ahead along the wall's own cross direction, so the player stands right in front of its middle
    return { position: Vec.add(position, Vec.fromPolar(angle - halfπ, distance)), angle };
};

/** what's in the way, as the message the player sees ("Can't build: a tree is in the way") */
export const buildBlockedMessage = (definition: Pick<ObstacleDefinition, "material" | "isWall" | "isTree"> & { readonly idString?: string }): InventoryMessages => {
    if (definition.idString === BUILD.wall) return InventoryMessages.BuildBlockedWall;
    if (definition.isTree || definition.material === "tree") return InventoryMessages.BuildBlockedTree;
    if (definition.isWall) return InventoryMessages.BuildBlockedWall;
    switch (definition.material) {
        case "stone": return InventoryMessages.BuildBlockedRock;
        case "crate":
        case "cardboard": return InventoryMessages.BuildBlockedCrate;
        case "metal_light":
        case "metal_heavy":
        case "iron": return InventoryMessages.BuildBlockedMetal;
        case "wood":
        case "glass": return InventoryMessages.BuildBlockedWall;
        default: return InventoryMessages.BuildBlockedSomething;
    }
};
